import { ApplicationRef, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { URLS } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private availableLanguages = ['he', 'en'];
  private defaultLanguage = 'en';
  private _currentLanguage: string = 'en';

  constructor(private translate: TranslateService,
              private appRef: ApplicationRef,
              private http: HttpClient) {}

  /**
   * Fetch the authenticated user's stored language from the API.
   * Returns the language string ('en' | 'he') on success.
   */
  fetchServerLanguage(): Observable<{ code: number; data: { language: string } }> {
    return this.http.get<{ code: number; data: { language: string } }>(URLS.API + 'users/language');
  }

  /**
   * Persist the authenticated user's language choice to the API.
   */
  saveServerLanguage(lang: string): Observable<{ code: number; data: { language: string } }> {
    return this.http.patch<{ code: number; data: { language: string } }>(URLS.API + 'users/language', { language: lang });
  }

  /**
   * Initialize the language service.
   * Resolution order: localStorage > navigator.language (he*) > 'en'.
   */
  init(): void {
    this.translate.addLangs(this.availableLanguages);
    this.translate.setDefaultLang(this.defaultLanguage);

    const saved = this.getSavedLanguage();
    const chosen = (saved && this.availableLanguages.includes(saved))
      ? saved
      : this.detectFromBrowser();

    this._currentLanguage = chosen;
    localStorage.setItem('language', chosen);
    this.applyDocumentLocale(chosen);
    this.translate.use(chosen).subscribe(
      () => {},
      (error) => console.error('Translation loading error:', error)
    );
  }

  /**
   * Reflect the active language on the <html> element so the browser, screen
   * readers, and CSS that targets [dir] all stay in sync.
   */
  private applyDocumentLocale(lang: string): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'he' ? 'rtl' : 'ltr';
  }

  /**
   * Pick a language from the browser locale. Returns 'he' only if the browser
   * locale explicitly starts with 'he'; defaults to 'en' otherwise.
   */
  private detectFromBrowser(): string {
    const nav = (
      (typeof navigator !== 'undefined' && (navigator.language || (navigator as any).userLanguage)) || 'en'
    ).toLowerCase();
    return nav.startsWith('he') ? 'he' : this.defaultLanguage;
  }

  /**
   * Get current language
   */
  getCurrentLanguage(): string {
    return this._currentLanguage;
  }

  /**
   * Set language and persist to localStorage
   */
  setLanguage(lang: string): void {
    if (this.availableLanguages.includes(lang)) {
      this._currentLanguage = lang;
      localStorage.setItem('language', lang);
      this.applyDocumentLocale(lang);
      this.translate.use(lang).subscribe(() => {
        this.appRef.tick();
      });
    }
  }

  /**
   * Get available languages
   */
  getAvailableLanguages(): string[] {
    return this.availableLanguages;
  }

  /**
   * Get saved language from localStorage
   */
  private getSavedLanguage(): string | null {
    return localStorage.getItem('language');
  }

  /** The user's stored language choice (independent of what's currently applied). */
  getChosenLanguage(): string {
    return this.getSavedLanguage() || this._currentLanguage;
  }

  /** Persist a deliberate language choice (localStorage + server). Does not apply. */
  setChosenLanguage(lang: string): void {
    if (!this.availableLanguages.includes(lang)) { return; }
    localStorage.setItem('language', lang);
    this.saveServerLanguage(lang).subscribe(() => {}, () => {});
  }

  /** Apply an effective language to the view without persisting it as the choice. */
  applyEffective(lang: string): void {
    this._currentLanguage = lang;
    this.applyDocumentLocale(lang);
    this.translate.use(lang).subscribe(() => this.appRef.tick());
  }

  /**
   * Toggle between Hebrew and English
   */
  toggleLanguage(): void {
    const currentLang = this.getCurrentLanguage();
    const newLang = currentLang === 'he' ? 'en' : 'he';
    this.setLanguage(newLang);
  }

  /**
   * Get language display name
   */
  getLanguageDisplayName(lang: string): string {
    const names: { [key: string]: string } = {
      'he': 'עברית',
      'en': 'English'
    };
    return names[lang] || lang;
  }
}
